import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';

const downloadButtons = [
  { icon: 'ri-windows-fill', label: '下载客户端' },
  { icon: 'ri-android-fill', label: '下载安卓端' },
  { icon: 'ri-computer-line', label: '体验网页版' },
  { icon: 'ri-apple-fill', label: '下载iOS版' },
];

const features = [
  {
    icon: 'ri-time-line',
    title: '7×24 小时云托管',
    description: '摆脱设备限制，全天候在线运行，游戏应用永不下线',
    color: 'bg-sky-500',
  },
  {
    icon: 'ri-speed-line',
    title: '高效与稳定齐发',
    description: '原生 ARM 架构，低延迟高兼容，畅玩主流手游应用',
    color: 'bg-emerald-500',
  },
  {
    icon: 'ri-stack-line',
    title: '多种模式满足不同需求',
    description: '支持批量管理、一键同步操作，多开业务轻松掌控',
    color: 'bg-cyan-500',
  },
  {
    icon: 'ri-shield-check-line',
    title: '云端数据安全保障',
    description: '云端安全存储，数据加密传输，全方位守护隐私',
    color: 'bg-indigo-500',
  },
];

const appGrid = [
  { icon: 'ri-gamepad-line', label: '云游戏', color: 'from-emerald-400 to-emerald-500' },
  { icon: 'ri-stack-line', label: '应用多开', color: 'from-sky-400 to-sky-500' },
  { icon: 'ri-briefcase-line', label: '移动办公', color: 'from-violet-400 to-violet-500' },
  { icon: 'ri-folder-cloud-line', label: '云端存储', color: 'from-amber-400 to-orange-500' },
];

const dockApps = [
  { icon: 'ri-phone-line', color: 'bg-emerald-500' },
  { icon: 'ri-message-3-line', color: 'bg-sky-500' },
  { icon: 'ri-camera-line', color: 'bg-violet-500' },
  { icon: 'ri-settings-3-line', color: 'bg-gray-500' },
];

export default function CloudPhonePage() {
  return (
    <div className="min-h-screen bg-[#f6fefb] pt-16">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[auto] lg:min-h-[92vh] flex items-center overflow-hidden bg-gradient-to-br from-[#e8fff6] via-[#f0fdfa] to-[#ecfeff]">
          <div className="absolute top-20 right-0 w-full lg:w-1/2 h-full opacity-30 pointer-events-none hidden lg:block">
            <div className="w-full h-full bg-gradient-to-l from-emerald-100/50 to-transparent"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12 sm:py-16 lg:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Content */}
              <div className="text-center lg:text-left order-2 lg:order-1">
                <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white/80 border border-emerald-100 text-emerald-600 text-xs sm:text-sm font-medium mb-4 sm:mb-6 shadow-sm">
                  <i className="ri-smartphone-line mr-1.5 sm:mr-2"></i>
                  徒影云手机 — 云端之上的全新体验
                </div>

                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                  <span className="block">随时随地</span>
                  <span className="block text-emerald-500 mt-1 sm:mt-2">畅玩云端手机</span>
                </h1>

                <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                  基于 ARM 云端虚拟化技术，7×24 小时在线托管，手游挂机、应用多开、移动办公一键搞定
                </p>

                {/* CTA Buttons */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-6 sm:mb-8 max-w-md mx-auto lg:mx-0">
                  {downloadButtons.map((btn, index) => (
                    <button
                      key={index}
                      className="flex flex-col items-center justify-center gap-1.5 sm:gap-2 bg-[#1c1c1e] hover:bg-gray-800 text-white rounded-xl sm:rounded-2xl py-3 sm:py-4 px-2 sm:px-3 transition-colors duration-200"
                    >
                      <i className={`${btn.icon} text-xl sm:text-2xl`}></i>
                      <span className="text-xs sm:text-sm font-medium whitespace-nowrap">{btn.label}</span>
                    </button>
                  ))}
                </div>

                <Link
                  to="/arm-cloud"
                  className="inline-flex items-center text-gray-700 hover:text-emerald-600 font-medium transition-colors text-sm sm:text-base"
                >
                  了解企业级 ARM 云服务 <i className="ri-arrow-right-line ml-2"></i>
                </Link>

                {/* Stats */}
                <div className="mt-8 sm:mt-12 grid grid-cols-3 gap-3 sm:gap-6 max-w-sm mx-auto lg:mx-0">
                  <div className="text-center p-3 bg-white/60 rounded-xl">
                    <div className="text-lg sm:text-2xl font-bold text-emerald-500 mb-0.5 sm:mb-1">7×24</div>
                    <div className="text-xs sm:text-sm text-gray-600">小时在线</div>
                  </div>
                  <div className="text-center p-3 bg-white/60 rounded-xl">
                    <div className="text-lg sm:text-2xl font-bold text-sky-500 mb-0.5 sm:mb-1">99.9%</div>
                    <div className="text-xs sm:text-sm text-gray-600">稳定可用</div>
                  </div>
                  <div className="text-center p-3 bg-white/60 rounded-xl">
                    <div className="text-lg sm:text-2xl font-bold text-emerald-500 mb-0.5 sm:mb-1">低延迟</div>
                    <div className="text-xs sm:text-sm text-gray-600">流畅体验</div>
                  </div>
                </div>
              </div>

              {/* Right Visual - Phone Mockup */}
              <div className="flex items-center justify-center order-1 lg:order-2 mb-8 lg:mb-0">
                <div className="relative">
                  <div className="w-56 sm:w-64 lg:w-72 h-[420px] sm:h-[480px] lg:h-[540px] bg-gray-900 rounded-[2rem] lg:rounded-[2.5rem] shadow-2xl border-[6px] lg:border-8 border-gray-800 flex flex-col overflow-hidden">
                    {/* Notch */}
                    <div className="h-5 sm:h-6 bg-gray-800 flex items-center justify-center shrink-0">
                      <div className="w-16 sm:w-20 h-1 bg-gray-600 rounded-full"></div>
                    </div>
                    {/* Screen */}
                    <div className="flex-1 bg-gradient-to-br from-emerald-50 via-sky-50 to-white flex flex-col">
                      {/* Status Bar */}
                      <div className="px-4 sm:px-5 pt-2 sm:pt-3 pb-1.5 sm:pb-2 flex items-center justify-between text-xs text-gray-600">
                        <span>9:41</span>
                        <div className="flex items-center gap-1">
                          <i className="ri-wifi-line"></i>
                          <i className="ri-battery-line"></i>
                        </div>
                      </div>

                      {/* Header */}
                      <div className="px-4 sm:px-5 pb-3 sm:pb-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm sm:text-base font-bold text-gray-800">徒影云手机</span>
                          <i className="ri-cloud-line text-emerald-500"></i>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">云端之上的全新体验</p>
                      </div>

                      {/* App Grid */}
                      <div className="flex-1 px-4 sm:px-5 py-2">
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                          {appGrid.map((app, index) => (
                            <div key={index} className="flex flex-col items-center gap-1.5 sm:gap-2">
                              <div className={`w-13 h-13 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${app.color} flex items-center justify-center shadow-md`}>
                                <i className={`${app.icon} text-xl sm:text-2xl text-white`}></i>
                              </div>
                              <span className="text-xs text-gray-700 font-medium">{app.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Bottom Dock */}
                      <div className="mx-3 sm:mx-4 mb-3 sm:mb-4 p-2 sm:p-3 bg-white/80 backdrop-blur-sm rounded-2xl flex items-center justify-around">
                        {dockApps.map((app, index) => (
                          <div key={index} className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl ${app.color} flex items-center justify-center`}>
                            <i className={`${app.icon} text-base sm:text-lg text-white`}></i>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Decorative elements */}
                  <div className="absolute -top-4 sm:-top-6 -right-4 sm:-right-6 w-16 sm:w-20 h-16 sm:h-20 bg-emerald-200 rounded-full opacity-60 blur-xl"></div>
                  <div className="absolute -bottom-4 sm:-bottom-6 -left-4 sm:-left-6 w-20 sm:w-24 h-20 sm:h-24 bg-sky-200 rounded-full opacity-60 blur-xl"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 sm:py-20 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 sm:mb-12 lg:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">为什么选择徒影云手机</h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                专为个人用户与小型团队打造的云端手机服务，稳定、安全、易用
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-slate-50 rounded-2xl p-5 sm:p-6 lg:p-8 hover:shadow-lg transition-shadow duration-300 border border-gray-100"
                >
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 ${feature.color} rounded-xl flex items-center justify-center mb-4 sm:mb-6`}>
                    <i className={`${feature.icon} text-xl sm:text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{feature.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quick Start CTA */}
        <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-emerald-50 to-sky-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">立即开启云端体验</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-8 sm:mb-10">
              注册即送免费试用时长，支持网页端、安卓、iOS 及 Windows 客户端多端同步
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-base sm:text-lg font-medium transition-colors">
                免费试用云手机
              </button>
              <button className="bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-base sm:text-lg font-medium transition-colors">
                查看价格方案
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div className="col-span-1 sm:col-span-2 md:col-span-2">
              <div className="flex items-center space-x-2 mb-3 sm:mb-4">
                <div className="w-8 h-8 bg-emerald-500 rounded-md flex items-center justify-center">
                  <i className="ri-smartphone-line text-white text-lg"></i>
                </div>
                <span className="text-lg sm:text-xl font-bold">徒影云手机</span>
              </div>
              <p className="text-gray-400 max-w-md text-sm sm:text-base">
                基于 ARM 架构的云端手机服务，为用户提供全天候在线、高效稳定的云端移动体验
              </p>
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">产品服务</h3>
              <ul className="space-y-2 text-gray-400 text-sm sm:text-base">
                <li className="hover:text-white cursor-pointer">云手机托管</li>
                <li className="hover:text-white cursor-pointer">游戏多开</li>
                <li className="hover:text-white cursor-pointer">应用测试</li>
                <li className="hover:text-white cursor-pointer">移动办公</li>
              </ul>
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">支持</h3>
              <ul className="space-y-2 text-gray-400 text-sm sm:text-base">
                <li className="hover:text-white cursor-pointer">帮助中心</li>
                <li className="hover:text-white cursor-pointer">下载中心</li>
                <li className="hover:text-white cursor-pointer">在线客服</li>
                <li className="hover:text-white cursor-pointer">意见反馈</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-6 sm:pt-8 text-center text-gray-500 text-xs sm:text-sm">
            © 2024 壹码云（上海）科技有限公司. 保留所有权利
          </div>
        </div>
      </footer>
    </div>
  );
}
